import React, { useState, useCallback, useMemo, memo, useRef, useEffect } from 'react';

import classNames from 'classnames';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { generateId } from '@shared/helpers';
import { UiButton } from '@shared/ui';

import cls from './MultiSelect.module.css';
import { Icon } from '../Icon';
import { Option } from './Option/Option';
import { useSetInputFocus } from '../../hooks';

export interface OptionType {
    value: number;
    title: string;
}

interface MultiSelectProps {
    selected: OptionType[];
    options: OptionType[];
    placeholder: string;
    deleteHandle: (value: string) => void;
    onChange: (value: string) => void;
    onAddNewTag?: (newTag: { value: number; title: string }) => void;
    title?: string;
    isRequired?: boolean;
    isDisabled?: boolean;
    error?: string | null;
    classes?: string;
    selectClasses?: string;
    showCheckboxes?: boolean;
    leftIcon?: React.ReactNode;
    enableCreateTags?: boolean;
    inputPlaceholder?: string;
    emptySearchTitle?: string;
    onReset?: () => void;
    renderAddOption?: (value: string, addNewTag: (newOption?: string) => void) => React.ReactNode;
    isSmartPosition?: boolean;
}

// todo слишком много логики для shared-компонента, рефактор в будущем

export const MultiSelect = memo((props: MultiSelectProps) => {
    const { t } = useTranslation();

    const {
        selected,
        options,
        placeholder,
        deleteHandle,
        onChange,
        onAddNewTag,
        title,
        isRequired,
        isDisabled = false,
        error = null,
        classes,
        showCheckboxes = false,
        leftIcon,
        enableCreateTags = true,
        selectClasses,
        onReset,
        renderAddOption,
        isSmartPosition = false,
        emptySearchTitle = t('MultiSelect.tag_not_found'),
        inputPlaceholder = t('MultiSelect.props.inputPlaceholder.default_value'),
    } = props;

    const [currentTagValue, setCurrentTagValue] = useState(generateId(true) as number);
    const [isOpen, setIsOpen] = useState(false);
    const [optionFilter, setOptionFilter] = useState('');
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    const rootRef = useRef<HTMLDivElement>(null);
    const selectWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        const calcRect = () => {
            if (!isSmartPosition) {
                setDropdownStyle({});
                return;
            }

            if (!selectWrapperRef.current) return;

            const rect = selectWrapperRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUpward = spaceBelow < 170;

            setDropdownStyle({
                position: 'fixed',
                top: openUpward ? rect.top - 8 : rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                transform: openUpward ? 'translateY(-100%)' : 'none',
            });
        };

        calcRect();

        window.addEventListener('resize', calcRect);
        window.addEventListener('scroll', calcRect, true);
        return () => {
            window.removeEventListener('resize', calcRect);
            window.removeEventListener('scroll', calcRect, true);
        };
    }, [isOpen, isSmartPosition]);

    const inputRef = useSetInputFocus({ isOpen });

    const filteredOptions = useMemo(() => {
        if (optionFilter === '') {
            return options;
        }
        const filtered = options.filter((option) =>
            option.title.toLowerCase().includes(optionFilter.toLowerCase())
        );
        return filtered.length ? filtered : [{ value: 'null', title: emptySearchTitle }];
    }, [optionFilter, options]);

    const handleOptionClick = useCallback(
        (value: string) => {
            if (showCheckboxes) {
                const isAlreadySelected = selected.some((item) => item.value.toString() === value);
                if (isAlreadySelected) {
                    deleteHandle(value);
                } else {
                    onChange(value);
                }
                return;
            }
            onChange(value);
            setIsOpen(false);
            setOptionFilter('');
        },
        [onChange, deleteHandle, selected, showCheckboxes]
    );

    const handlePlaceholderClick = useCallback(() => {
        if (!isDisabled) {
            setIsOpen((prev) => !prev);
        }
    }, [isDisabled]);

    const isNewTagEqualsOptionFilter = useMemo(
        () => filteredOptions.some((item) => item.title === optionFilter),
        [optionFilter, filteredOptions]
    );

    const handleOptionFilter = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setOptionFilter(event.target.value);
    }, []);

    const addNewTag = useCallback(
        (newOption?: string) => {
            const newTag = {
                value: currentTagValue,
                title: newOption || optionFilter,
                isChecked: true,
            };
            onAddNewTag(newTag);
            setCurrentTagValue(generateId(true) as number);
            setIsOpen(false);
            setOptionFilter('');
        },
        [onAddNewTag, optionFilter, currentTagValue]
    );

    const handleDelete = useCallback(
        (event: React.MouseEvent, value: string) => {
            event.stopPropagation();
            deleteHandle(value);
        },
        [deleteHandle]
    );

    return (
        <div ref={rootRef} className={classNames(cls.formBlockContent, classes)}>
            {title && (
                <label className={classNames(cls.modalLabelSelect, { [cls.disabled]: isDisabled })}>
                    {title}
                    {isRequired && <span>*</span>}
                </label>
            )}
            <div
                onClick={handlePlaceholderClick}
                className={classNames(cls.selectWrapper, selectClasses, {
                    [cls.disabled]: isDisabled,
                    [cls.WrapperIsOpen]: isOpen,
                    [cls.active]: isOpen,
                    [cls.withLeftIcon]: !!leftIcon,
                })}
                data-is-active={isOpen}
                ref={selectWrapperRef}
            >
                {leftIcon && <div className={cls.leftIcon}>{leftIcon}</div>}
                <div className={cls.selectedWrapper}>
                    {selected.length === 0 ? (
                        <span className={cls.PlaceholderText}>{placeholder}</span>
                    ) : (
                        selected.map((option) => (
                            <div
                                key={option.value}
                                className={classNames(cls.selected, {
                                    [cls.disabled]: isDisabled,
                                })}
                            >
                                <span
                                    onClick={(e) => handleDelete(e, option.value.toString())}
                                    className={cls.delete}
                                >
                                    ×
                                </span>
                                <span className={cls.selectedSpan}>{option.title}</span>
                            </div>
                        ))
                    )}
                </div>
                {!isDisabled && (
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    <>
                        {onReset ? (
                            <button onClick={onReset} className={cls.resetIcon}>
                                <Icon iconName="resetInput" />
                            </button>
                        ) : (
                            <div
                                className={classNames(cls.arrow, {
                                    [cls.openArrow]: isOpen,
                                })}
                            >
                                <Icon iconName="selectArrow" />
                            </div>
                        )}
                    </>
                )}
            </div>
            {isOpen && (
                <div
                    className={cn(
                        cls.OpenedTagsContainer,
                        title && !isSmartPosition && cls.withTitle
                    )}
                    style={dropdownStyle}
                >
                    <div className={cls.filterInput} onClick={(e) => e.stopPropagation()}>
                        <input
                            ref={inputRef}
                            className={cls.input}
                            onChange={handleOptionFilter}
                            value={optionFilter}
                            placeholder={inputPlaceholder}
                            maxLength={40}
                        />
                    </div>
                    <ul className={cls.optionsUl}>
                        {filteredOptions[0]?.value === 'null' ? (
                            <div className={cls.noResults}>
                                <span className={cls.noResultsTitle}>
                                    {filteredOptions[0]?.title}
                                </span>
                                {enableCreateTags && !renderAddOption && (
                                    <UiButton
                                        onClick={() => addNewTag()}
                                        className={cls.AddNewTag}
                                        variant="text"
                                        startIcon={<Icon iconName="plus" color="#1094F7" />}
                                        endIcon={
                                            <span className={cls.ButtonText}>
                                                {t('MultiSelect.return.create')} "{optionFilter}"
                                            </span>
                                        }
                                    />
                                )}
                            </div>
                        ) : (
                            <div className="">
                                {filteredOptions.map((option) => (
                                    <Option
                                        isActive={selected.some(
                                            (item) => item.value === option.value
                                        )}
                                        key={option.value}
                                        option={{
                                            title: option.title,
                                            value: option.value.toString(),
                                        }}
                                        onClick={handleOptionClick}
                                        showCheckboxes={showCheckboxes}
                                        styles={cls.option}
                                    />
                                ))}
                                {enableCreateTags &&
                                    !renderAddOption &&
                                    optionFilter.length > 0 &&
                                    !isNewTagEqualsOptionFilter && (
                                        <div style={{ paddingInline: '8px', marginTop: '12px' }}>
                                            <UiButton
                                                onClick={() => addNewTag()}
                                                className={cls.AddNewTag}
                                                variant="text"
                                                startIcon={<Icon iconName="plus" color="#1094F7" />}
                                                endIcon={
                                                    <span className={cls.ButtonText}>
                                                        {t('MultiSelect.return.create')} "
                                                        {optionFilter}"
                                                    </span>
                                                }
                                            />
                                        </div>
                                    )}
                            </div>
                        )}
                        {renderAddOption && <>{renderAddOption(optionFilter, addNewTag)}</>}
                    </ul>
                </div>
            )}
            {error && <span className={cls.errorMessage}>{error}</span>}
        </div>
    );
});
